import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersService, type UsersListParams, type UsersListResponse } from '../services/usersService';

// Ключи для кеша
export const usersKeys = {
  all: ['users'] as const,
  list: (params: UsersListParams = {}) => [...usersKeys.all, 'list', params] as const,
};

// Placeholder данные для мгновенного отображения UI
const placeholderUsersResponse: UsersListResponse = {
  data: [],
  current_page: 1,
  last_page: 1,
  per_page: 25,
  total: 0,
};

/**
 * Хук для получения списка пользователей
 */
export const useUsers = (params: UsersListParams = {}, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = usersKeys.list(params);
  
  // Получаем данные из кеша синхронно
  const cachedData = queryClient.getQueryData<UsersListResponse>(cacheKey);
  // Проверяем похожие запросы (без поиска или с другими параметрами пагинации) для использования в качестве fallback
  const similarCacheKey = usersKeys.list({ ...params, search: undefined, page: 1 });
  const similarCachedData = queryClient.getQueryData<UsersListResponse>(similarCacheKey);
  
  // Начальные данные берем ТОЛЬКО из реального кеша.
  // Если подставить placeholder как initialData, React Query может посчитать
  // такие данные «свежими» и не выполнить запрос при первом открытии.
  const initialData = cachedData;
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => usersService.getUsers(params),
    enabled, // Обновлять только когда включено
    staleTime: 1000 * 30, // 30 секунд - данные считаются свежими
    gcTime: 1000 * 60 * 30, // 30 минут - время хранения в кеше
    refetchInterval: enabled ? 1000 * 30 : false, // Автоматическое обновление каждые 30 секунд только если включено
    refetchIntervalInBackground: true, // Обновлять даже когда вкладка неактивна
    // Используем кешированные данные как placeholder для мгновенного отображения
    placeholderData: (previousData) => {
      // Если есть предыдущие данные из запроса, используем их
      if (previousData) return previousData;
      // Иначе используем кеш или похожий кеш
      return cachedData ?? similarCachedData ?? placeholderUsersResponse;
    },
    // Начальные данные для мгновенного отображения - только из кеша
    initialData: initialData,
    initialDataUpdatedAt: cachedData ? Date.now() - 1000 : undefined,
    // Обновлять при монтировании (если данных нет или они устарели)
    refetchOnMount: true,
  });
  
  // Всегда возвращаем данные (из кеша, placeholder или реальные)
  // Приоритет: реальные данные > кеш > похожий кеш > placeholder
  const data = query.data ?? cachedData ?? similarCachedData ?? placeholderUsersResponse;
  
  return {
    ...query,
    data,
    // isLoading только если нет данных вообще и идет загрузка
    isLoading: query.isLoading && !cachedData && !similarCachedData && !query.data,
  };
};
